import React from 'react';
import { Check, ChevronDown, ChevronUp, Pencil, X } from 'lucide-react';
import Button from '@/components/button/Button';
import LinkButton from '@/components/button/LinkButton';
import { Switch } from "@/components/forms/Switch";
import { Tooltip } from "@/components/tooltips/Tooltip";
import HolidayInclusionSelect from "@/components/forms/HolidayInclusionSelect";
import type { DayInfo, RoleInfo, RolesTableRowProps } from './roleInterfaces';
import RolesTableCell from './RolesTableCell';
import type { SpecificNeedsConstraints } from '@/types/needsContrains';
import * as Dialog from '@radix-ui/react-dialog';

import type { NeedsConstraintsExceptionItem } from '@/types/needsContrainsRequest.ts';

type PreparedDayInfo = {
    day: DayInfo,
    dayId: string,
    holidayException: NeedsConstraintsExceptionItem | undefined;
    skipHoliday: boolean | undefined;
    config: SpecificNeedsConstraints;
    hasDayOverride: boolean;
    defaultConfig: SpecificNeedsConstraints;
}
type PreparedDayInfoReturn = {
    weekHolidayState: RoleInfo["holiday"]
    hasDayWeekOverrides: boolean,
    map: { [dayId: string]: PreparedDayInfo },
    list: PreparedDayInfo[]
}


const ExceptionModal = function (props: { preparedDayInfos: PreparedDayInfoReturn }) {
    const preparedDayInfos = props.preparedDayInfos;

    const weekState = preparedDayInfos.weekHolidayState;
    let message: React.ReactNode = ""
    let filteredDays = preparedDayInfos.list;

    if (weekState === "Exclure") {
        filteredDays = preparedDayInfos.list.filter(d =>
            d.config.exceptions?.holiday?.actionIfMatch === "skip" && d.config.exceptions?.holiday?.active !== "false"
        );
    } else if (weekState === "Seulement") {
        filteredDays = preparedDayInfos.list.filter(d =>
            d.config.exceptions?.holiday?.actionIfNoMatch === "skip" && d.config.exceptions?.holiday?.active !== "false"
        );
    }

    const dayElements = filteredDays.map((d, index) => {
        let separator = "";

        if (index > 0) {
            separator = index === filteredDays.length - 1 ? " ou " : ", ";
        }

        return (
            <React.Fragment key={d.dayId}>
                {separator}
                <span className="font-bold">{d.day.name.toLowerCase()}</span>
            </React.Fragment>
        );
    });

    const isAllDays = filteredDays.length >= 7;

    if (weekState === "Exclure") {
        if (isAllDays) {
            message = "Pour tous les jours, si la journée est un férié, alors aucun besoin.";
        } else {
            message = (
                <span>
                    Si {dayElements}, et que la journée est un férié, alors aucun besoin.
                </span>
            );
        }
    } else if (weekState === "Seulement") {
        if (isAllDays) {
            message = "Pour tous les jours, si la journée n'est PAS un férié, alors aucun besoin.";
        } else {
            message = (
                <span>
                    Si {dayElements}, et que la journée n'est PAS un férié, alors aucun besoin.
                </span>
            );
        }
    }

    if (!message) return null;

    return <Dialog.Root>
        <Dialog.Trigger asChild>
            <button>
                <span className="text-xs text-red-600 font-normal font-bold">Autres exceptions</span>
            </button>
        </Dialog.Trigger>
        <Dialog.Portal>
            <Dialog.Overlay className="DialogOverlay fixed inset-0 bg-black/50"/>
            <Dialog.Content
                className="DialogContent fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-8 text-gray-900 shadow">
                <Dialog.Title className="DialogTitle font-bold text-xl">Exceptions Fériés</Dialog.Title>
                <Dialog.Description className="DialogDescription">
                    <div className='h-4'></div>
                    {/*dayOvrExcludeDayLabels.map((str)=>{
                        return <div>• {str}</div>
                        })*/}
                    {/*dayOvrExcludeDayLabels.length ?   <div className='py-2'>ou</div>  : ''*/}
                    <div></div>
                    {message}
                </Dialog.Description>
                <Dialog.Close asChild className='p-8 relative top-4 min-w-20'
                              style={{ margin: "8px 0 4px auto", justifyItems: "right" }}>
                    <Button style={{ justifyContent: "center" }} type="primary" label={'OK'}/>
                </Dialog.Close>
            </Dialog.Content>
        </Dialog.Portal>
    </Dialog.Root>
}


function prepareRoleDataForDaysMap(currentRole: RoleInfo, days: DayInfo[], weekHolidayState: RoleInfo["holiday"]): PreparedDayInfoReturn {
    const daysMap: { [dayId: string]: PreparedDayInfo } = {}
    const daysList: PreparedDayInfo[] = [];
    let hasDayWeekOverrides = false;

    days.forEach(day => {
        const dateStr = day.date;

        const defaultConfig: SpecificNeedsConstraints = {
            active: "true",
            apply: "true",
            start: 0,
            end: 0,
            min: 0,
            max: 0
        };

        let hasDayOverride: boolean = !!(currentRole.dicOfSpecificDays?.[dateStr]);
        let dayOverride = currentRole.dicOfSpecificDays?.[dateStr];
        let config: SpecificNeedsConstraints = defaultConfig;

        if (hasDayOverride && dayOverride!.active !== "false") {

            config = dayOverride || defaultConfig;
        } else {
            hasDayOverride = false;
            config = (currentRole.dicOfDaysOfWeek?.[day.dayIndex]) || defaultConfig;
        }

        const holidayException = config.exceptions?.holiday;
        const skipHoliday = holidayException && holidayException.active !== "false" && holidayException.actionIfMatch === "skip";

        if (hasDayOverride) hasDayWeekOverrides = true;
        //@ Populate map..

        daysList.push(daysMap[dateStr] = {
            day: day,
            dayId: dateStr,
            holidayException,
            skipHoliday,
            config,
            hasDayOverride,
            defaultConfig
        });

    });

    return {
        list: daysList,
        map: daysMap,
        hasDayWeekOverrides,
        weekHolidayState
    };
}


export function NeedRowsToggleButton({ expanded, onToggle, disabled = false }: Readonly<{ expanded: boolean; onToggle: () => void; disabled?: boolean }>) {
    // A disabled button receives no mouse events, so the explanation sits on the wrapper.
    return (
        <span title={disabled ? "Terminer l'édition avant de replier" : undefined} className="inline-flex">
            <button
                type="button"
                onClick={onToggle}
                disabled={disabled}
                aria-expanded={expanded}
                title={disabled ? undefined : (expanded ? "Replier" : "Déplier")}
                className="cursor-pointer rounded p-0.5 text-gray-600 hover:bg-gray-100 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
            >
                {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
            </button>
        </span>
    );
}

export default function RolesTableRow(
    {
        role,
        isEditing,
        editForm,
        days,
        onEdit,
        onSave,
        onCancel,
        onConfigChange,
        onRoleApplyToggle,
        onRoleLabelClick,
        needLabel,
        showRoleCell = true,
        roleCellRowSpan,
        needRowsToggle,
        markHolidays = false
    }: Readonly<RolesTableRowProps>) {
    const currentRole = isEditing && editForm ? editForm : role;
    const preparedDayInfoMap = prepareRoleDataForDaysMap(currentRole, days, currentRole.holiday);
    const SHOW_NEEDS_IDEAL = true;
    const SHOW_ACTIVATED = isEditing;
    const SHOW_EQUITY = false;
    // With a dedicated need key per holiday state, the per-role holiday selector has no meaning.
    const isLegacyHolidayMode = !currentRole.needId;
    const SHOW_HOLIDAY = isEditing && isLegacyHolidayMode;
    const SHOW_EXCEPTIONS = isLegacyHolidayMode && (currentRole.holiday !== "Inclure" || preparedDayInfoMap.hasDayWeekOverrides) && !isEditing;
    const applyRole = currentRole.apply !== "false";
    const inactiveStyle = applyRole ? "" : "opacity-50 pointer-events-none grayscale";

    return (
        <tr>
            {showRoleCell && <td className="border border-gray-300 p-1 font-bold align-middle bg-white" rowSpan={roleCellRowSpan}>
                <div className="flex flex-col items-center gap-2">
                    {onRoleLabelClick ? (
                        <LinkButton
                            onClick={() => onRoleLabelClick(role)}
                            title="Saisir les exceptions de besoin sur toute la période"
                            className="font-bold"
                        >
                            {role.label}
                        </LinkButton>
                    ) : (
                        <span>{role.label}</span>
                    )}
                    {SHOW_HOLIDAY ? (isEditing ? (
                        <HolidayInclusionSelect
                            value={currentRole.holiday}
                            onChange={(val) => onConfigChange("0", 'holiday', val)}
                            triggerClassName="w-full h-6 text-xs"
                            triggerStyle={{ height: "28px" }}
                        />
                    ) : (
                        <span className="text-xs text-blue-600 font-normal">{currentRole.holiday} férié</span>
                    )) : ''}
                    {SHOW_EXCEPTIONS ? <ExceptionModal preparedDayInfos={preparedDayInfoMap}></ExceptionModal> : ''}

                </div>
            </td>}
            {needLabel !== undefined &&
                <td className="border border-gray-300 align-middle text-xs font-bold text-gray-900 bg-white px-2 whitespace-nowrap">
                    <div className="flex items-center justify-between gap-3">
                        <span className={inactiveStyle}>{needLabel}</span>
                        {needRowsToggle && (
                            <NeedRowsToggleButton expanded={needRowsToggle.expanded} onToggle={needRowsToggle.onToggle} disabled={needRowsToggle.disabled}/>
                        )}
                    </div>
                </td>}
            <td className={`border border-gray-300 align-top text-xs text-gray-500 mt-1 bg-white text-right p-0 ${inactiveStyle}`}>
                <div className="h-8 flex items-center justify-end font-medium border-b border-gray-100 px-2 whitespace-nowrap">Mode:</div>
                {SHOW_ACTIVATED ?
                    <div className="h-8 flex items-center justify-end font-medium border-b border-gray-100 px-2 whitespace-nowrap">
                        Activé:
                    </div> : ''}
                {SHOW_NEEDS_IDEAL ?
                    <div className="h-8 flex items-center justify-end font-medium border-b border-gray-100 px-2 whitespace-nowrap">
                        Besoins vs Idéal:
                    </div> : ''}
                <div className="h-8 flex items-center justify-end font-medium border-b border-gray-100 px-2 whitespace-nowrap">
                    Heures:
                </div>
                {SHOW_EQUITY ?
                    <div className="h-8 flex items-center justify-end font-medium border-b border-gray-100 px-2 whitespace-nowrap">
                        {/* Poid équité */}
                    </div> : ''}
            </td>
            {
                days.map(day => {
                    const dateStr = day.date;
                    const { config, hasDayOverride, skipHoliday } = preparedDayInfoMap.map[dateStr]!;

                    return (
                        <RolesTableCell
                            key={day.date}
                            day={day}
                            config={config}
                            isEditing={isEditing}
                            hasDayOverride={hasDayOverride}
                            skipHoliday={skipHoliday}
                            onConfigChange={(d, field, val, isExc) => onConfigChange(d, field, val, isExc, dateStr)}
                            isHoliday={markHolidays && day.isHoliday}
                            showActive={SHOW_ACTIVATED}
                            showNeedsIdeal={SHOW_NEEDS_IDEAL}
                            showQ={SHOW_EQUITY}
                            isRoleApplied={applyRole}
                        />
                    );
                })
            }

            <td className="p-2 border border-gray-300 align-middle">
                <div className="flex flex-col items-center gap-2">
                    {isEditing ? (
                        <div className="flex flex-col gap-2">
                            <Tooltip content="Enregistrer les changements" placement="left">
                                <Button type="success" icon={<Check size={16}/>} onClick={onSave}/>
                            </Tooltip>
                            <Tooltip content="Annuler les changements" placement="left">
                                <Button type="danger" icon={<X size={16}/>} onClick={onCancel}/>
                            </Tooltip>
                        </div>
                    ) : (
                        <Tooltip content="Modifier" placement="left">
                            <Button type="primary" icon={<Pencil size={16}/>} onClick={() => onEdit(role)}/>
                        </Tooltip>
                    )}
                    <Tooltip content={applyRole ? "Désactiver" : "Activer"} placement="left">
                        <Switch
                            checked={applyRole}
                            onChange={(checked) => onRoleApplyToggle(currentRole, checked.toString())}
                            className="scale-75"
                        />
                    </Tooltip>
                </div>
            </td>
        </tr>
    );
}
